/* eslint-disable @typescript-eslint/no-explicit-any */
import { MenuItem } from '@mui/material'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import DropdownButton from 'components/buttons/DropdownButton'
import { PLACEHOLDER } from 'components/column-renderers'
import { PRIMARY_ID_MISSING } from 'const'

export type RowActionRenderer = {
  renderName: (value: ICellRendererParams<any>) => string
  onClick: (id: number, props: ICellRendererParams<any>) => void
}

export type RowAction = (
  | {
      name: string
      hide?: boolean
      onClick: (id: number, props: ICellRendererParams<any>) => void
    }
  | RowActionRenderer
) & {
  hideCondition?: (value: ICellRendererParams<any>) => boolean
}

export type ActionColumnLoadingState = {
  isLoading: boolean
  loadingId?: number | null
  loadingIdField?: string
}

export const getActionColumn = (
  rowActions: RowAction[],
  idField: string = 'id',
  loadingState?: ActionColumnLoadingState,
): ColDef => {
  return {
    headerName: 'Action',
    pinned: 'right',
    suppressMovable: true,
    filter: null,
    minWidth: 120,
    width: 120,
    maxWidth: 120,
    resizable: false,
    sortable: false,
    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    cellRenderer: (props: ICellRendererParams<{ [idField: string]: number }>) => {
      const itemId = props?.data?.[idField]
      const loadingIdField = loadingState?.loadingIdField ?? idField
      const rowLoadingId = props?.data?.[loadingIdField]
      const isRowLoading = Boolean(
        loadingState?.isLoading &&
          loadingState.loadingId != null &&
          rowLoadingId === loadingState.loadingId,
      )

      const actions = rowActions.map((it) => {
        let name = ''
        if ('name' in it) {
          name = it.name
        }
        if ('renderName' in it) {
          name = it.renderName(props)
        }
        let hide = false
        if ('hideCondition' in it && it.hideCondition) {
          hide = it.hideCondition(props)
        }

        return {
          name: name,
          hide,
          onClick: () => {
            if (!itemId) {
              console.error(PRIMARY_ID_MISSING)
              return
            }
            it.onClick(itemId, props)
          },
        }
      })

      // Every action hidden for this row — render a placeholder instead of a button that
      // opens an empty menu.
      if (actions.every((action) => action.hide)) {
        return PLACEHOLDER
      }

      return (
        <DropdownButton
          buttonTitle="Actions"
          variant="outlined"
          loading={isRowLoading}
          disabled={isRowLoading}
        >
          {actions.map((action) => {
            if (action.hide) return null

            return (
              <MenuItem key={action.name} onClick={action.onClick}>
                {action.name}
              </MenuItem>
            )
          })}
        </DropdownButton>
      )
    },
  }
}
